import {BehaviorSubject, Observable, ReplaySubject, Scheduler, Subject} from "rxjs";
import {ConsolidatedOrderDepthRecord} from "../types/OrderDepthRecord";
import {DepthService} from "../../services/depth/DepthService";
import {OrderDepthViewModel} from "../models/OrderDepthViewModel";
import {OrderDepthModels} from "../models/OrderDepthModels";
import {CodeAndExchange, securityEquality} from "../../instruments/types/CodeAndExchange";
import {OrderDepthExpansionModel} from "../models/OrderDepthExpansionModel";
import {OrderDepthViewModelFactory} from "../service/OrderDepthViewModelFactory";
import {
    applyToggle,
    askDepthToggle,
    bidDepthToggle,
    OrderDepthStoreExpansionToggle
} from "./internal/OrderDepthStoreExpansionToggle";
import {ThemeType} from "../../types/UserConfig";
import {SystemEventService} from "../../services/system-state/SystemEventService";
import {IdleEvent, SystemEvent} from "../../services/system-state/SystemEvent";
import {CurrentUserService} from "../../services/user/CurrentUserService";
import {ForceLiveDepthService} from "../../services/depth/ForceLiveDepthService";

const QueryIntervalSeconds = 15;
const QueryInterval = QueryIntervalSeconds * 1000;

export class OrderDepthStore {

    private readonly currentCode$ = new ReplaySubject<CodeAndExchange>(1);
    private readonly displayAllRows$ = new Subject<boolean>();
    private readonly defaultRowCount$ = new BehaviorSubject(Number.MAX_SAFE_INTEGER);
    private readonly theme$ = new ReplaySubject<ThemeType>(1);

    private readonly expansion$ = new ReplaySubject<OrderDepthExpansionModel>(1);
    private readonly depth$: Observable<OrderDepthViewModel>;
    private readonly bids$: Observable<OrderDepthModels>;
    private readonly asks$: Observable<OrderDepthModels>;

    private readonly expansionToggle$ = new Subject<OrderDepthStoreExpansionToggle>();

    private readonly updated$ = new BehaviorSubject(true);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    public constructor(private readonly depthService: DepthService | ForceLiveDepthService,
                       private readonly eventService: SystemEventService,
                       private readonly currentUserService: CurrentUserService) {
        this.depth$ = this.depthSource();
        this.bids$ = this.buildBids();
        this.asks$ = this.buildAsks();
    }

    private depthSource(): Observable<OrderDepthViewModel> {
        return this.currentCode$
                   .distinctUntilChanged(securityEquality())
                   .switchMap((code) => this.buildDepth(code))
                   .publishReplay(1)
                   .refCount();
    }

    private buildDepth(security: CodeAndExchange): Observable<OrderDepthViewModel> {
        const depth$ = this.retrieveDepthUpdates(security);
        const expansion$ = this.expansionModel();
        const rowsToDisplay$ = this.displayedRows();
        const source$ = Observable.combineLatest(depth$, expansion$, rowsToDisplay$, this.theme$);

        return source$.throttleTime(100)
                      .map(([[previous, current], expansion, rowsToDisplay, theme]) => {
                          return OrderDepthViewModelFactory.build(current!, previous, expansion, rowsToDisplay, theme);
                      });
    }

    private retrieveDepthUpdates(code: CodeAndExchange) {
        const backup$ = Observable.of(1).delay(QueryInterval);

        const active$ = this.eventService
                            .idle()
                            .map((evt) => evt === SystemEvent.IDLE_END)
                            .startWith(true)
                            .distinctUntilChanged();

        const loggedIn$ = this.currentUserService
                              .current
                              .map((u) => u.isLoggedIn)
                              .startWith(true)
                              .distinctUntilChanged();

        const shouldPoll$ = Observable.combineLatest(active$, loggedIn$)
                                      .map(([active, loggedIn]) => active && loggedIn)
                                      .distinctUntilChanged();

        return this.updated$
                   .switchMap(() => backup$)
                   .startWith(0)
                   .withLatestFrom(shouldPoll$)
                   .switchMap(([_, poll]) => this.loadDepth(code, poll))
                   .startWith(null)
                   .pairwise();
    }

    private loadDepth(code: CodeAndExchange, poll: boolean): Observable<ConsolidatedOrderDepthRecord> {
        if (poll) {
            return Observable.of(code)
                             .flatMap((c) => this.depthService.load(c))
                             .finally(() => this.updated$.next(true))
                             .catch(() => Observable.empty<ConsolidatedOrderDepthRecord>());
        } else {
            return Observable.empty<ConsolidatedOrderDepthRecord>()
                             .finally(() => this.updated$.next(true));
        }
    }

    private expansionModel(): Observable<OrderDepthExpansionModel> {
        const initialExpansion = OrderDepthExpansionModel.empty();
        return this.expansionToggle$
                   .scan((acc, next) => applyToggle(acc, next), initialExpansion)
                   .startWith(initialExpansion);
    }

    private displayedRows(): Observable<number> {
        return this.defaultRowCount$
                   .switchMap((defaultRowCount) => {
                       return this.displayAllRows$
                                  .map(() => Number.MAX_SAFE_INTEGER)
                                  .startWith(defaultRowCount)
                                  .distinctUntilChanged();
                   });
    }

    private buildBids(): Observable<OrderDepthModels> {
        return this.depth$
                   .observeOn(Scheduler.async)
                   .map((d) => d.bids)
                   .publishReplay(1)
                   .refCount();
    }

    private buildAsks(): Observable<OrderDepthModels> {
        return this.depth$
                   .observeOn(Scheduler.async)
                   .map((d) => d.asks)
                   .publishReplay(1)
                   .refCount();
    }

    public get currentCode(): Observable<CodeAndExchange> {
        return this.currentCode$.observeOn(Scheduler.async);
    }

    public updateCode(code: CodeAndExchange) {
        this.expansion$.next(OrderDepthExpansionModel.empty());
        this.currentCode$.next(code);
    }

    public updateDefaultRowCount(rows: number) {
        this.defaultRowCount$.next(rows);
    }

    public updateTheme(theme: ThemeType) {
        this.theme$.next(theme);
    }

    public get depth(): Observable<OrderDepthViewModel> {
        return this.depth$.observeOn(Scheduler.async);
    }

    public get bids(): Observable<OrderDepthModels> {
        return this.bids$.observeOn(Scheduler.async);
    }

    public get asks(): Observable<OrderDepthModels> {
        return this.asks$.observeOn(Scheduler.async);
    }

    public toggleBid(bid: number) {
        const change = bidDepthToggle(bid);
        this.expansionToggle$.next(change);
    }

    public toggleAsk(ask: number) {
        const change = askDepthToggle(ask);
        this.expansionToggle$.next(change);
    }

    public showHiddenRows() {
        this.displayAllRows$.next();
    }

    public destroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
